import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal } from "@/components/Primitives";
import { PRICING, BUSINESS } from "@/data/site";

export default function Pricing() {
  return (
    <>
      <Seo
        title="Pricing & Memberships"
        description="Simple, transparent memberships from Mom's Best Friend Sitters. Family Starter $39/mo, Family Pro $59/mo. $20/hr sitter pay. Pricing for reference."
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "Pricing", item: "/pricing" }]}
      />
      <PageHero
        overline="Pricing & memberships"
        title="Transparent pricing, no surprises"
        subtitle="Choose a membership that fits your family's rhythm. Pricing shown is for reference — our team confirms every detail with you personally."
      />

      <section className="container-mbf py-20 md:py-28">
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {PRICING.map((tier, i) => (
            <Reveal key={tier.id} delay={i * 0.08}>
              <div className={`rounded-2xl p-9 border h-full flex flex-col ${tier.featured ? "bg-espresso text-parchment border-espresso shadow-warm-lg" : "bg-white border-[#F1E6D0] shadow-warm"}`} data-testid={`pricing-card-${tier.id}`}>
                {tier.featured && <span className="text-xs uppercase tracking-widest text-gold font-semibold mb-2">Most popular</span>}
                <h2 className={`font-serif text-2xl ${tier.featured ? "text-white" : "text-espresso"}`}>{tier.name}</h2>
                <div className="mt-4 mb-2">
                  <span className="text-5xl font-serif">{tier.price}</span>
                  <span className={tier.featured ? "text-parchment/70" : "text-stoneish"}>{tier.period}</span>
                </div>
                <p className={`text-sm mb-6 ${tier.featured ? "text-parchment/80" : "text-stoneish"}`}>{tier.desc}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${tier.featured ? "bg-gold/20 text-gold" : "bg-sage/15 text-sage"}`}><Check className="w-3 h-3" /></span>
                      <span className={tier.featured ? "text-parchment/90" : "text-espresso"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/book" className={tier.featured ? "btn-accent w-full" : "btn-primary w-full"} data-testid={`pricing-cta-${tier.id}`}>
                  Get started <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 max-w-4xl mx-auto grid sm:grid-cols-3 gap-4 text-center">
          {[
            { label: "Sitter pay", value: "$20/hr" },
            { label: "Additional child", value: "+$1/hr" },
            { label: "Extra booking", value: "+$15" },
          ].map((item) => (
            <div key={item.label} className="bg-parchment-2 rounded-xl border border-[#F1E6D0] p-6" data-testid={`pricing-detail-${item.label}`}>
              <div className="font-serif text-2xl text-espresso">{item.value}</div>
              <div className="text-xs uppercase tracking-wider text-stoneish mt-1">{item.label}</div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-stoneish mt-8 max-w-xl mx-auto">
          No online payments — pricing is displayed for reference. A {BUSINESS.minHours}-hour minimum applies to every booking.
        </p>

        <div className="text-center mt-6">
          <Link to="/pricing/benefits" className="btn-primary" data-testid="pricing-benefits-link">Explore membership benefits <ArrowRight className="w-4 h-4" /></Link>
        </div>

        <div className="text-center mt-10">
          <a href={BUSINESS.phoneHref} className="btn-outline" data-testid="pricing-phone-link"><Phone className="w-4 h-4" /> Questions? Call {BUSINESS.phone}</a>
        </div>
      </section>
    </>
  );
}
